if (!customElements.get('aov-mobile-bundles')) {
  customElements.define('aov-mobile-bundles', class extends HTMLElement {
    connectedCallback() {
      this.root = document.getElementById(this.dataset.productInfo);
      if (!this.root || this.observer) return;
      try {
        this.config = JSON.parse(this.querySelector('script').textContent);
      } catch (_) { return; }
      this.views = new Map();
      this.mobile = matchMedia('(max-width: 749px)');
      this.onBreakpoint = () => this.sync();
      this.mobile.addEventListener('change', this.onBreakpoint);
      this.observer = new MutationObserver(() => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
      });
      this.observer.observe(this.root, {
        childList: true, subtree: true, characterData: true, attributes: true,
        attributeFilter: ['aria-checked', 'class']
      });
      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      cancelAnimationFrame(this.frame);
      this.mobile?.removeEventListener('change', this.onBreakpoint);
      this.views?.forEach(view => this.clearView(view));
      if (this.upsellAnchor?.isConnected && this.upsell?.isConnected) {
        this.upsellAnchor.after(this.upsell);
      }
      this.upsellAnchor?.remove();
    }

    clearView(view) {
      view.tabs.remove();
      view.container.classList.remove('pets-aov-mobile-tabs');
      view.group.setAttribute('role', view.groupRole);
      view.items.forEach(item => {
        item.classList.remove('pets-aov-mobile-panel', 'pets-aov-mobile-panel--selected');
        item.removeAttribute('role');
        item.removeAttribute('aria-labelledby');
        if (item.id?.startsWith(`${this.dataset.productInfo}-mobile-offer-`)) item.removeAttribute('id');
      });
    }

    sync() {
      this.views.forEach((view, container) => {
        if (!this.root.contains(container)) { this.clearView(view); this.views.delete(container); }
      });
      if (this.config.enabled) {
        this.root.querySelectorAll('.pets-aov-offers').forEach(container => this.syncTabs(container));
      }
      this.positionUpsell();
    }

    syncTabs(container) {
      const group = container.querySelector('.Avada-VolumeItem__Container');
      if (!group) return;
      const items = [...group.children].filter(node => node.matches('.Avada-Volume__Item'));
      const selected = items.findIndex(item => item.querySelector('.Avada-Volume__Content[aria-checked="true"]'));
      let view = this.views.get(container);
      if (view && (view.group !== group || view.items.length !== items.length || view.items.some((item, i) => item !== items[i]))) {
        this.clearView(view); this.views.delete(container); view = null;
      }
      // Fail open to the original cards if the app has not finished rendering selection.
      if (selected < 0 || items.length < 2) {
        if (view) { this.clearView(view); this.views.delete(container); }
        return;
      }
      if (!view) {
        const tabs = document.createElement('div');
        tabs.className = 'pets-aov-mobile-tablist';
        tabs.setAttribute('role', 'tablist');
        tabs.setAttribute('aria-label', this.config.label || 'Choose your bundle');
        const labels = (this.config.labels || '').split('|').map(label => label.trim());
        const prefix = `${this.dataset.productInfo}-mobile-offer-${this.views.size}`;
        items.forEach((item, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.id = `${prefix}-tab-${index}`;
          button.className = 'pets-aov-mobile-tab';
          button.setAttribute('role', 'tab');
          item.id ||= `${prefix}-panel-${index}`;
          button.setAttribute('aria-controls', item.id);
          const label = document.createElement('span');
          label.className = 'pets-aov-mobile-tab__label';
          label.textContent = labels[index] || item.querySelector('.Avada-Volume__Info--TriggerQty > span')?.textContent || `Offer ${index + 1}`;
          const price = document.createElement('span');
          price.className = 'pets-aov-mobile-tab__price';
          price.hidden = true;
          button.append(label, price);
          // Click the original AOV control: never calculate or replace its purchase payload.
          button.addEventListener('click', () => item.querySelector('.Avada-Volume__Content')?.click());
          tabs.append(button);
        });
        tabs.addEventListener('keydown', event => {
          const buttons = [...tabs.children];
          const index = buttons.indexOf(event.target);
          if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
            : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
          buttons[next].focus();
          buttons[next].click();
        });
        group.before(tabs);
        view = { container, group, items, tabs, groupRole: group.getAttribute('role') || 'radiogroup' };
        this.views.set(container, view);
      }
      if (!container.classList.contains('pets-aov-mobile-tabs')) container.classList.add('pets-aov-mobile-tabs');
      const groupRole = this.mobile.matches ? 'presentation' : view.groupRole;
      if (group.getAttribute('role') !== groupRole) group.setAttribute('role', groupRole);
      items.forEach((item, index) => {
        if (!item.classList.contains('pets-aov-mobile-panel')) item.classList.add('pets-aov-mobile-panel');
        item.classList.toggle('pets-aov-mobile-panel--selected', index === selected);
        const button = view.tabs.children[index];
        this.updateUnitPrice(button, item, index);
        const isSelected = String(index === selected);
        if (button.getAttribute('aria-selected') !== isSelected) button.setAttribute('aria-selected', isSelected);
        button.tabIndex = index === selected ? 0 : -1;
        if (this.mobile.matches) {
          item.setAttribute('role', 'tabpanel');
          item.setAttribute('aria-labelledby', button.id);
        } else {
          item.removeAttribute('role');
          item.removeAttribute('aria-labelledby');
        }
      });
    }

    updateUnitPrice(button, item, index) {
      const price = button.querySelector('.pets-aov-mobile-tab__price');
      const quantity = Number((this.config.quantities || '').split(',')[index]);
      let text = '';
      if (Number.isInteger(quantity) && quantity > 0 && this.config.currency && window.PetsUpsellOffers?.readMoney) {
        const total = item.querySelector('.AOV-Offer__DiscountPrice')?.textContent || '';
        const cents = window.PetsUpsellOffers.readMoney(total, this.config.currency);
        if (Number.isFinite(cents) && cents >= 0 && total.trim()) {
          const formatted = new Intl.NumberFormat(this.config.locale || 'en', {
            style: 'currency', currency: this.config.currency
          }).format(Math.round(cents / quantity) / 100);
          text = quantity === 1 ? formatted : `${formatted} ${this.config.eachLabel || 'EA'}`;
        }
      }
      if (price.textContent !== text) price.textContent = text;
      price.hidden = !text;
    }

    positionUpsell() {
      if (!this.config.moveUpsell) return;
      const upsell = this.root.querySelector('product-metafield-upsell');
      const reassurance = this.root.querySelector('.product-information-grid--reassurance');
      if (!upsell || !reassurance) return;
      if (upsell !== this.upsell) {
        this.upsellAnchor?.remove();
        this.upsell = upsell;
        this.upsellAnchor = document.createComment('Original desktop upsell position');
        upsell.before(this.upsellAnchor);
      }
      const destination = this.mobile.matches ? reassurance : this.upsellAnchor;
      if (destination.nextSibling === upsell) return;
      // Reuse the same component and inputs, retaining add-on choices across breakpoints.
      const index = upsell.index;
      const focused = upsell.contains(document.activeElement) ? document.activeElement : null;
      destination.after(upsell);
      if (Number.isInteger(index)) { upsell.index = index; upsell.update?.(); }
      focused?.focus({ preventScroll: true });
    }
  });
}
